/**
 * Card de um PLANO na carteira MPA (GUPM-332) — cabeçalho com o contexto do
 * plano + as ações como subcards.
 *
 * O contexto herdado (Barragem · Nível · TARP · instrumento) aparece UMA vez,
 * no cabeçalho; antes era repetido em cada ação do plano.
 *
 * O card INTEIRO abre o `PlanoDetail` (decisão do dono, 26/09) — menos os
 * controles de dentro dele (toggle de concluídas, Iniciar/Concluir do RT), que
 * fazem só o que dizem. Sem overlay `absolute inset-0`: ele seria um retângulo
 * invisível disputando camada com esses botões. O clique é tratado no `<article>`
 * e ignorado quando nasce num controle; o cabeçalho segue sendo o `<button>` que
 * dá ao teclado e ao leitor de tela o mesmo caminho.
 */
import { useState, type MouseEvent } from 'react';
import { LEVEL_UI, type TarpLevel } from '@/ui';
import { type AcaoStatus } from '@/domain/planos';
import { rotuloTarpDeOrigem, separarConcluidasDoGrupo, type GrupoPlano } from '@/domain/planos/mpa';
import { formatarDiaDoInstante } from '@/domain/fuso-brasilia';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';
import { MpaAcaoSubcard } from './MpaAcaoSubcard';

function nivelUi(nivel: string | null) {
  return nivel && nivel in LEVEL_UI ? LEVEL_UI[nivel as TarpLevel] : null;
}

interface Props {
  grupo: GrupoPlano;
  hoje: Date;
  canEdit: boolean;
  updating: boolean;
  onOpenPlano: (planoId: string) => void;
  onUpdateAcao: (planoId: string, acaoId: string, status: AcaoStatus) => void;
}

export function MpaPlanoCard({ grupo, hoje, canEdit, updating, onOpenPlano, onUpdateAcao }: Props) {
  const [verConcluidas, setVerConcluidas] = useState(false);
  const { ativas, recolhiveis } = separarConcluidasDoGrupo(grupo);
  const lv = nivelUi(grupo.nivel);
  const origem = rotuloTarpDeOrigem(grupo);
  const visiveis = verConcluidas ? [...ativas, ...recolhiveis] : ativas;

  // Clique fora de qualquer controle abre o detalhe. O do cabeçalho também cai aqui
  // (o `<button>` não tem handler próprio), então a abertura acontece uma vez só.
  // O teclado chega pelo `<button>` do cabeçalho (Enter/Espaço disparam click, que
  // sobe até aqui); o article é só o atalho de ponteiro para o mesmo destino.
  function abrirSeForForaDeControle(e: MouseEvent<HTMLElement>) {
    const controle = (e.target as HTMLElement).closest('button, a, input, select, textarea');
    if (controle && !controle.hasAttribute('data-abre-plano')) return;
    onOpenPlano(grupo.planoId);
  }

  return (
    <article
      onClick={abrirSeForForaDeControle}
      className="group cursor-pointer rounded-xl border border-jf-border bg-jf-surface transition hover:border-jf-yellow/60"
    >
      <button
        type="button"
        data-abre-plano
        aria-label={`Abrir detalhes do plano ${grupo.codigo}${grupo.damCode ? ` — ${rotuloDaEstrutura(grupo.damCode)}` : ''}${lv ? ` · ${lv.label}` : ''} · ${grupo.tarpSeq != null ? `TARP ${origem}` : origem} — ${grupo.totalAcoes} ${grupo.totalAcoes === 1 ? 'ação' : 'ações'}`}
        className="w-full rounded-t-xl px-3.5 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-jf-yellow/70"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {grupo.damCode && (
              <span className="rounded-md border border-jf-border/60 bg-jf-border/20 px-1.5 py-0.5 text-[11px] font-semibold text-jf-muted">
                {rotuloDaEstrutura(grupo.damCode)}
              </span>
            )}
            {lv && (
              <span
                className="rounded-full border px-2 py-0.5 text-[11px] font-bold"
                style={{ color: lv.color, borderColor: `${lv.color}66`, background: `${lv.color}1a` }}
              >
                {lv.icon} {lv.label}
              </span>
            )}
            {/* A TARP que abriu o plano, pelo número da lista do M3. O protocolo ao
                lado (TARP-INA-AL) é o TIPO — igual para toda TARP daquele tipo. */}
            <span
              title="TARP de origem"
              className={
                grupo.tarpSeq != null
                  ? 'rounded-md border border-jf-yellow/50 bg-jf-yellow/10 px-1.5 py-0.5 font-mono text-[11px] font-bold text-jf-yellow'
                  : 'text-[11px] italic text-jf-dim'
              }
            >
              {origem}
            </span>
            {(grupo.tarpCode || grupo.instrumento) && (
              <span className="font-mono text-xs text-jf-muted">
                {[grupo.tarpCode, grupo.instrumento].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <span className="text-lg leading-none text-jf-dim transition group-hover:text-jf-yellow" aria-hidden="true">
            ›
          </span>
        </div>

        <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5">
          <span className="font-mono text-sm font-bold text-white">{grupo.codigo}</span>
          <span className="text-[11px] text-jf-dim">
            {grupo.concluidasTotal}/{grupo.totalAcoes} concluídas
          </span>
          <span className="text-[11px] text-jf-muted">Criado em {formatarDiaDoInstante(grupo.criadoEm)}</span>
        </div>
      </button>

      <div className="border-t border-jf-border p-2.5">
        <ul className="space-y-2">
          {visiveis.map((a) => (
            <MpaAcaoSubcard
              // Chave composta, como na lista achatada anterior: os ids de ação NÃO são
              // globalmente únicos (o seed repete "1"/"2" entre planos distintos). Aqui
              // os irmãos são sempre do mesmo plano, então `a.id` bastaria — mas a
              // composta mantém a chave correta se este map algum dia achatar planos.
              key={`${a.planoId}:${a.id}`}
              acao={a}
              hoje={hoje}
              canEdit={canEdit}
              updating={updating}
              onUpdateAcao={onUpdateAcao}
            />
          ))}
        </ul>

        {recolhiveis.length > 0 && (
          <button
            type="button"
            onClick={() => setVerConcluidas((v) => !v)}
            aria-expanded={verConcluidas}
            // O rótulo acompanha o estado (dizer "Mostrar" com o conteúdo já à vista
            // confunde leitor de tela) e nomeia o PLANO: com vários cards mistos na
            // tela, "deste plano" deixaria N botões com nome acessível idêntico.
            aria-label={`${verConcluidas ? 'Ocultar' : 'Mostrar'} ações concluídas do plano ${grupo.codigo}`}
            className="mt-2 text-[11px] font-bold text-white transition hover:text-jf-yellow"
          >
            {verConcluidas ? '▴ ocultar' : '▾ ver'} {recolhiveis.length}{' '}
            {recolhiveis.length === 1 ? 'concluída' : 'concluídas'}
          </button>
        )}
      </div>
    </article>
  );
}
